/**
 * @fileoverview The Beds pop-up (D78 P2, mock-up frame 4): "Add a bed" with the same two rows as Plants' pop-up.
 * "Take or upload a photo" ("Of the bed, or of its seed packet(s). Grow separates and names what's in it; you name the
 * bed and save.") opens the photo path for a new bed: each card in "Check and save" starts on "A new bed…" (a photo
 * of packets named from its label when it has one, as Add bed's packet scan did; a photo of the bed itself, the kinds
 * Grow sees, made into the bed with the photo as its cover, P4). "Type it in" ("Its name, what kind of bed it is, its
 * sun and cover.") opens today's bed dialog, which no longer has its dashed packet row: the photo row covers it.
 *
 * Where: LocationGrid (the add placeholders, the empty Beds page).
 * API: the photo path's (usePhotoAdd).
 * Words (en/es): locations.form.newTitle (the pop-up's title), add.bed.photoHint, add.bed.photoHintNoAi,
 * add.bed.typeHint.
 *
 * @module useAddBed
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { Filed } from '@/features/photo-tray/components/TrayPiles';
import { AddChooser } from './AddWays';
import { usePhotoAdd } from './usePhotoAdd';

/** What the pop-up is told. */
interface AddBedOptions {
  /** "Type it in": the page's bed dialog. */
  onType: () => void;
  /** Told of every filing from photos. */
  onFiled?: (filed: Filed) => void;
}

/** What the hook hands back. */
export interface AddBedControl {
  /** Opens the pop-up. */
  open: () => void;
  /** The pop-up and the photo path: render it once. */
  element: React.ReactNode;
}

/**
 * The Beds pop-up and both its ways.
 *
 * @param options - AddBedOptions.
 * @returns AddBedControl.
 */
export function useAddBed({ onType, onFiled }: AddBedOptions): AddBedControl {
  const { reactTranslate: t } = useTranslation();
  const [choosing, setChoosing] = React.useState(false);
  const photoAdd = usePhotoAdd({ newBed: true, onFiled });
  const element = (
    <>
      <AddChooser
        open={choosing}
        onOpenChange={setChoosing}
        title={t('locations.form.newTitle')}
        photoHint="add.bed.photoHint"
        photoHintNoAi="add.bed.photoHintNoAi"
        typeHint="add.bed.typeHint"
        onPhoto={() => { setChoosing(false); photoAdd.start(); }}
        onType={() => { setChoosing(false); onType(); }}
      />
      {photoAdd.element}
    </>
  );
  return { open: () => setChoosing(true), element };
}
